import { env } from './env';
import { supabase } from './supabase';

/**
 * Standard envelope returned by every Communicate Edge Function.
 * Mirrors `_shared/errors.ts` on the server side (Clinio V2 `web/src/lib/api.ts`).
 */
export type ApiSuccess<T> = { ok: true; data: T };
export type ApiError = {
  code:
    | 'unauthorized'
    | 'forbidden'
    | 'invalid-input'
    | 'not-found'
    | 'conflict'
    | 'rate-limited'
    | 'method-not-allowed'
    | 'gone'
    | 'token-invalid'
    | 'internal-error';
  message: string;
  details?: unknown;
};
export type ApiFailure = { ok: false; error: ApiError };
export type ApiResponse<T> = ApiSuccess<T> | ApiFailure;

export class ApiCallError extends Error {
  readonly code: ApiError['code'];
  readonly details?: unknown;
  constructor(error: ApiError) {
    super(error.message);
    this.code = error.code;
    this.details = error.details;
    this.name = 'ApiCallError';
  }
}

/**
 * GET a path under `ops-dashboard`.
 * `supabase.functions.invoke` uses POST; this helper does a raw fetch for GET
 * (Clinio V2 `usePayTransactions` / `useClinicPay` pattern).
 */
export async function fetchOpsDashboard<T>(
  resource: 'messages' | 'threads' | 'acknowledgements' | 'medbin-previews',
  params: Record<string, string | undefined> = {},
): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, v);
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';

  const resp = await fetch(
    `${env.VITE_SUPABASE_URL}/functions/v1/ops-dashboard/${resource}${suffix}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${session?.access_token ?? ''}`,
        apikey: env.VITE_SUPABASE_ANON_KEY,
        'x-application-name': 'clinio-communicate-web',
      },
    },
  );

  const json = (await resp.json().catch(() => null)) as ApiResponse<T> | null;

  if (!resp.ok || !json) {
    if (json && json.ok === false) {
      throw new ApiCallError(json.error);
    }
    throw new ApiCallError({
      code: 'internal-error',
      message: `ops-dashboard/${resource} failed (${resp.status})`,
    });
  }
  if (json.ok === false) {
    throw new ApiCallError(json.error);
  }
  return json.data;
}
